import { html, LitElement, nothing, CSSResultGroup } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { Attribute } from 'components/discovery/variant-filter/typing/product-types.js'
import { VariantOption } from 'components/discovery/variant-filter/typing/shared-types.js'
import { getDisplayType } from './display-types/variant-filter-strategies.js'
import { isDropdownFilter, VARIANT_FILTER_STORE, VariantFilterStore } from 'components/discovery/variant-filter/variant-filter-store.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createUrl } from '@cloudsuite/mosaic-shared/scripts/base-url.js'
import { productFilterAttributesPath, productVariantFilterPath } from 'components/discovery/shared/endpoint-paths.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './variant-filter.style.js'

type SkeletonShape = 'circle' | 'square' | 'box'

const SKELETON_SIZES = {
  label: 20,
  circle: 25,
  square: 60,
  boxMinWidth: 60,
  boxPaddingInline: 50,
  boxCharWidth: 9,
  select: 40,
  showMore: { width: 30, height: 20 },
}

type SkeletonAttribute = {
  name: string,
  display_mode?: string,
  options: { name: string }[],
}

/** Shown while the attributes are not known yet: a color row and a text row. */
const FALLBACK_SKELETON_ATTRIBUTES: SkeletonAttribute[] = [
  { display_mode: 'property_color', name: 'Color', options: Array.from({ length: 5 }, () => ({ name: '' })) },
  { display_mode: 'property_text', name: 'Size', options: Array.from({ length: 4 }, () => ({ name: '00' })) },
]

const skeletonShapeFor = (displayMode: string | undefined): SkeletonShape | 'select' => {
  switch (displayMode) {
    case 'property_color': return 'circle'
    case 'property_image':
    case 'product_image': return 'square'
    case 'property_text': return 'box'
    default: return 'select'
  }
}

@customElement('csc-variant-filter')
export class CSCVariantFilter extends LitElement {
  static override styles: CSSResultGroup = [style]

  @property({ type: Number, attribute: 'product-id' })
  productId: number | null = null

  // Todo, move to translations
  @property({ type: String, attribute: 'choose-filter-text' })
  chooseFilterText: string = ''

  @property({ type: Number, attribute: 'display-limit' })
  displayLimit: number = Infinity

  @property({ type: Boolean, attribute: 'loading' })
  loading = false

  @property({ type: String, attribute: 'skeleton-variant' })
  skeletonVariant: 'primary' | 'secondary' = 'secondary'

  @inject(VARIANT_FILTER_STORE)
  store!: VariantFilterStore

  @state()
  private variantOptions: Attribute[] = []

  @state()
  private fetchingAttributes = false

  private disposeVariantOptionsEffect: (() => void) | undefined = undefined

  override connectedCallback() {
    super.connectedCallback()

    this.disposeVariantOptionsEffect = createEffect(() => {
      this.variantOptions = this.store.variantOptions()
    })

    this.loadAttributes().catch((error: unknown) => {
      console.error('Could not load variant filter attributes', error)
    })
  }

  private async loadAttributes() {
    this.store.setAttributesUrl(await this.productUrl(productFilterAttributesPath))

    const storefrontUrl = await this.productUrl(productVariantFilterPath)
    if (!storefrontUrl) return

    this.fetchingAttributes = true
    try {
      await this.store.fetchAttributes(storefrontUrl)
    } finally {
      this.fetchingAttributes = false
    }
  }

  private async productUrl(path: (productId: number) => string): Promise<string> {
    if (this.productId === null) return ''
    return (await createUrl(path(this.productId))).href
  }

  override disconnectedCallback() {
    super.disconnectedCallback()

    this.disposeVariantOptionsEffect?.()
    this.disposeVariantOptionsEffect = undefined
  }

  private selectOption = async (option: VariantOption) => {
    this.dispatchEvent(new VariantFilterOptionSelectedEvent())
    await this.store.optionSelected(option)
  }

  private hasNoCombinationParameter() {
    const paramsString = window.location.search
    const searchParams = new URLSearchParams(paramsString)

    return searchParams.has('othercombination')
  }

  private renderAttribute(attribute: Attribute, filterContainsDropdown: boolean) {
    const displayType = getDisplayType(attribute.display_mode)

    if (!displayType) {
      return html`<cse-text>unsupported display mode (${attribute.display_mode})</cse-text>`
    }

    return displayType({
      attribute,
      selectOption: this.selectOption,
      chooseFilterText: this.chooseFilterText,
      displayLimit: this.displayLimit,
      filterContainsDropdown,
    })
  }

  private renderSkeletonLoading() {
    const attributes: SkeletonAttribute[] = this.variantOptions.length > 0 ? this.variantOptions : FALLBACK_SKELETON_ATTRIBUTES
    let index = 0
    const skeleton = (className: string, width: number | null, height: number) => html`
      <cse-skeleton
        class="${className}"
        variant=${this.skeletonVariant}
        width=${width ?? nothing}
        height=${height}
        index=${index++}>
      </cse-skeleton>
    `

    return html`
      <div class="skeleton-loading" aria-busy="true">
        ${attributes.map(attribute => {
          const shape = skeletonShapeFor(attribute.display_mode)
          const labelWidth = Math.max(80, attribute.name.length * 9)

          if (shape === 'select') {
            return html`
              <div class="skeleton-field">
                ${skeleton('skeleton-label', labelWidth, SKELETON_SIZES.label)}
                ${skeleton('skeleton-select', null, SKELETON_SIZES.select)}
              </div>
            `
          }

          const visibleOptions = attribute.options.slice(0, this.displayLimit)
          const hasHiddenOptions = attribute.options.length > visibleOptions.length

          return html`
            <div class="skeleton-field">
              ${skeleton('skeleton-label', labelWidth, SKELETON_SIZES.label)}
              <div class="skeleton-group">
                ${visibleOptions.map(option => {
                  if (shape === 'circle') return skeleton('skeleton-circle', SKELETON_SIZES.circle, SKELETON_SIZES.circle)
                  if (shape === 'square') return skeleton('skeleton-square', SKELETON_SIZES.square, SKELETON_SIZES.square)
                  const boxWidth = Math.max(SKELETON_SIZES.boxMinWidth, option.name.length * SKELETON_SIZES.boxCharWidth + SKELETON_SIZES.boxPaddingInline)
                  return skeleton('skeleton-box', boxWidth, SKELETON_SIZES.square)
                })}
                ${hasHiddenOptions ? skeleton('skeleton-show-more', SKELETON_SIZES.showMore.width, SKELETON_SIZES.showMore.height) : nothing}
              </div>
            </div>
          `
        })}
      </div>
    `
  }

  override render() {
    // The options arrive before the fetch settles, so switch to the filters as soon as they are known.
    const awaitingAttributes = this.fetchingAttributes && this.variantOptions.length === 0
    if (this.loading || awaitingAttributes) {
      return this.renderSkeletonLoading()
    }

    const filterContainsDropdown = this.variantOptions.some(isDropdownFilter)

    return html`
      <cse-fieldset>
        ${this.hasNoCombinationParameter()
          ? html`
            <cse-alert variant="warning">
              <span slot="description">
                ${t('variant_result_no_combination', { ns: 'csc-variant-filter-result-no-combination', default: 'Notice! The combination of variants you have chosen is not available. We have selected another variant for you.' })}
              </span>
            </cse-alert>
            `
          : nothing
        }

        ${repeat(
          this.variantOptions,
          attribute => attribute.id,
          attribute => this.renderAttribute(attribute, filterContainsDropdown)
        )}
      </cse-fieldset>
    `
  }
}

export class VariantFilterOptionSelectedEvent extends Event {
  static type = 'variant-filter-option-selected' as const

  constructor() {
    super(VariantFilterOptionSelectedEvent.type, { bubbles: true, composed: true })
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-variant-filter': CSCVariantFilter
  }
}
